import { calcBenchmarkComparison, getBenchmarkKey } from './benchmark';

/** Default analytics filter state — all dashboard widgets derive from this. */
export const DEFAULT_ANALYTICS_FILTERS = {
  datePreset: 'today',
  taskType: 'all',
  search: '',
  statusScope: 'completed',
  workerName: '',
  roomId: '',
  taskCategoryId: '',
  tagId: '',
  productName: '',
  costMin: null,
  costMax: null,
  durationMin: null,
  durationMax: null,
  costPerUnitMin: null,
  costPerUnitMax: null,
  shift: '',
  hasIssueNotes: false,
  dataQuality: '',
  performanceMode: '',
  sortKey: 'submittedAt',
  sortDir: 'desc',
};

const VALID_DATE_PRESETS = new Set([
  'today', 'yesterday', 'thisWeek', 'thisMonth',
  'last3Months', 'last6Months', 'last365Days',
]);

const VALID_TASK_TYPES = new Set(['all', 'production', 'support', 'delay', 'other']);
const VALID_STATUS_SCOPES = new Set(['completed', 'active']);
const VALID_SHIFTS = new Set(['', 'morning', 'afternoon', 'evening', 'night']);
const VALID_DATA_QUALITY = new Set(['', 'missing_cost', 'missing_end', 'long_duration', 'any']);
const VALID_PERFORMANCE = new Set([
  '', 'high_cost', 'slowest', 'best_performing', 'over_expected_time', 'over_expected_cost',
]);
const VALID_SORT_KEYS = new Set([
  'submittedAt', 'startTime', 'taskName', 'taskType', 'durationMinutes', 'labourHours',
  'labourCost', 'costPerUnit', 'quantityCompleted', 'roomName', 'taskCategoryName',
  'employeeNames',
]);
const VALID_SORT_DIRS = new Set(['asc', 'desc']);

function percentile(values, p) {
  if (!values.length) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const idx = Math.ceil((p / 100) * sorted.length) - 1;
  return sorted[Math.max(0, idx)];
}

function taskShift(task) {
  const ts = task.startTime?.toDate?.() ?? (task.startTime ? new Date(task.startTime) : null);
  if (!ts || isNaN(ts)) return null;
  const h = ts.getHours();
  if (h >= 6 && h < 12) return 'morning';
  if (h >= 12 && h < 18) return 'afternoon';
  if (h >= 18) return 'evening';
  return 'night';
}

function taskSortValue(task, key) {
  if (key === 'employeeNames') {
    const names = task.employeeNames;
    return names?.length ? names[0].toLowerCase() : '';
  }
  let v = task[key];
  if (v?.toDate) return v.toDate().getTime();
  if (typeof v === 'string') return v.toLowerCase();
  return v ?? (typeof task[key] === 'number' ? -Infinity : '');
}

function matchesSearch(task, search) {
  if (!search) return true;
  const s = search.toLowerCase();
  return (
    task.taskName?.toLowerCase().includes(s) ||
    task.customTaskName?.toLowerCase().includes(s) ||
    task.productName?.toLowerCase().includes(s) ||
    task.lotNumber?.toLowerCase().includes(s) ||
    task.roomName?.toLowerCase().includes(s) ||
    task.taskCategoryName?.toLowerCase().includes(s) ||
    task.tagNames?.some((n) => n.toLowerCase().includes(s)) ||
    task.employeeNames?.some((n) => n.toLowerCase().includes(s))
  );
}

/** Sanitize and validate a filter object loaded from Firestore or presets. */
export function validateAnalyticsFilters(raw) {
  if (!raw || typeof raw !== 'object') return { ...DEFAULT_ANALYTICS_FILTERS };

  const num = (v) => (v != null && v !== '' && !Number.isNaN(Number(v)) ? Number(v) : null);

  let costMin = num(raw.costMin);
  let costMax = num(raw.costMax);
  if (costMin != null && costMax != null && costMin > costMax) {
    [costMin, costMax] = [costMax, costMin];
  }
  let durationMin = num(raw.durationMin);
  let durationMax = num(raw.durationMax);
  if (durationMin != null && durationMax != null && durationMin > durationMax) {
    [durationMin, durationMax] = [durationMax, durationMin];
  }

  return {
    datePreset: VALID_DATE_PRESETS.has(raw.datePreset) ? raw.datePreset : DEFAULT_ANALYTICS_FILTERS.datePreset,
    taskType: VALID_TASK_TYPES.has(raw.taskType) ? raw.taskType : 'all',
    search: typeof raw.search === 'string' ? raw.search.slice(0, 200) : '',
    statusScope: VALID_STATUS_SCOPES.has(raw.statusScope) ? raw.statusScope : 'completed',
    workerName: typeof raw.workerName === 'string' ? raw.workerName.slice(0, 100) : '',
    roomId: typeof raw.roomId === 'string' ? raw.roomId.slice(0, 100) : '',
    taskCategoryId: typeof raw.taskCategoryId === 'string' ? raw.taskCategoryId.slice(0, 100) : '',
    tagId: typeof raw.tagId === 'string' ? raw.tagId.slice(0, 100) : '',
    productName: typeof raw.productName === 'string' ? raw.productName.slice(0, 100) : '',
    costMin,
    costMax,
    durationMin,
    durationMax,
    costPerUnitMin: num(raw.costPerUnitMin),
    costPerUnitMax: num(raw.costPerUnitMax),
    shift: VALID_SHIFTS.has(raw.shift) ? raw.shift : '',
    hasIssueNotes: raw.hasIssueNotes === true,
    dataQuality: VALID_DATA_QUALITY.has(raw.dataQuality) ? raw.dataQuality : '',
    performanceMode: VALID_PERFORMANCE.has(raw.performanceMode) ? raw.performanceMode : '',
    sortKey: VALID_SORT_KEYS.has(raw.sortKey) ? raw.sortKey : 'submittedAt',
    sortDir: VALID_SORT_DIRS.has(raw.sortDir) ? raw.sortDir : 'desc',
  };
}

/** Built-in preset views grouped for the UI. */
export const PRESET_VIEW_GROUPS = [
  {
    id: 'time',
    labelKey: 'analyticsViews.groups.time',
    presets: [
      {
        id: 'completed',
        labelKey: 'analyticsViews.presets.completed',
        filters: { statusScope: 'completed', performanceMode: '', dataQuality: '', sortKey: 'submittedAt', sortDir: 'desc' },
      },
      {
        id: 'active',
        labelKey: 'analyticsViews.presets.active',
        filters: { statusScope: 'active', performanceMode: '', dataQuality: '', sortKey: 'startTime', sortDir: 'desc' },
      },
    ],
  },
  {
    id: 'people',
    labelKey: 'analyticsViews.groups.people',
    presets: [
      {
        id: 'byWorker',
        labelKey: 'analyticsViews.presets.byWorker',
        hintKey: 'analyticsViews.presetHints.byWorker',
        filters: { statusScope: 'completed', sortKey: 'employeeNames', sortDir: 'asc', performanceMode: '', dataQuality: '' },
      },
    ],
  },
  {
    id: 'product',
    labelKey: 'analyticsViews.groups.product',
    presets: [
      {
        id: 'byProductType',
        labelKey: 'analyticsViews.presets.byProductType',
        hintKey: 'analyticsViews.presetHints.byProductType',
        filters: { statusScope: 'completed', sortKey: 'taskCategoryName', sortDir: 'asc', performanceMode: '', dataQuality: '' },
      },
      {
        id: 'byTaskType',
        labelKey: 'analyticsViews.presets.byTaskType',
        hintKey: 'analyticsViews.presetHints.byTaskType',
        filters: { statusScope: 'completed', sortKey: 'taskType', sortDir: 'asc', taskType: 'all', performanceMode: '', dataQuality: '' },
      },
      {
        id: 'byRoom',
        labelKey: 'analyticsViews.presets.byRoom',
        hintKey: 'analyticsViews.presetHints.byRoom',
        filters: { statusScope: 'completed', sortKey: 'roomName', sortDir: 'asc', performanceMode: '', dataQuality: '' },
      },
    ],
  },
  {
    id: 'cost',
    labelKey: 'analyticsViews.groups.cost',
    presets: [
      {
        id: 'highCost',
        labelKey: 'analyticsViews.presets.highCost',
        filters: { statusScope: 'completed', performanceMode: 'high_cost', dataQuality: '', sortKey: 'labourCost', sortDir: 'desc' },
      },
      {
        id: 'overExpectedCost',
        labelKey: 'analyticsViews.presets.overExpectedCost',
        filters: { statusScope: 'completed', performanceMode: 'over_expected_cost', dataQuality: '', sortKey: 'labourCost', sortDir: 'desc' },
      },
    ],
  },
  {
    id: 'performance',
    labelKey: 'analyticsViews.groups.performance',
    presets: [
      {
        id: 'slowest',
        labelKey: 'analyticsViews.presets.slowest',
        filters: { statusScope: 'completed', performanceMode: 'slowest', dataQuality: '', sortKey: 'durationMinutes', sortDir: 'desc' },
      },
      {
        id: 'bestPerforming',
        labelKey: 'analyticsViews.presets.bestPerforming',
        filters: { statusScope: 'completed', performanceMode: 'best_performing', dataQuality: '', sortKey: 'durationMinutes', sortDir: 'asc' },
      },
      {
        id: 'overExpectedTime',
        labelKey: 'analyticsViews.presets.overExpectedTime',
        filters: { statusScope: 'completed', performanceMode: 'over_expected_time', dataQuality: '', sortKey: 'durationMinutes', sortDir: 'desc' },
      },
    ],
  },
  {
    id: 'dataQuality',
    labelKey: 'analyticsViews.groups.dataQuality',
    presets: [
      {
        id: 'missingData',
        labelKey: 'analyticsViews.presets.missingData',
        filters: { statusScope: 'completed', dataQuality: 'any', performanceMode: '', sortKey: 'submittedAt', sortDir: 'desc' },
      },
    ],
  },
];

export const ALL_PRESET_IDS = PRESET_VIEW_GROUPS.flatMap((g) => g.presets.map((p) => p.id));

export function getPresetById(presetId) {
  for (const group of PRESET_VIEW_GROUPS) {
    const preset = group.presets.find((p) => p.id === presetId);
    if (preset) return preset;
  }
  return null;
}

export function buildPresetFilters(presetId) {
  const preset = getPresetById(presetId);
  if (!preset) return { ...DEFAULT_ANALYTICS_FILTERS };
  return validateAnalyticsFilters({ ...DEFAULT_ANALYTICS_FILTERS, ...preset.filters });
}

/** Compare two filter states (ignoring sort for "modified" detection optional). */
export function filtersMatch(a, b, { ignoreSort = false } = {}) {
  const va = validateAnalyticsFilters(a);
  const vb = validateAnalyticsFilters(b);
  const keys = Object.keys(DEFAULT_ANALYTICS_FILTERS).filter(
    (k) => !ignoreSort || (k !== 'sortKey' && k !== 'sortDir')
  );
  return keys.every((k) => va[k] === vb[k]);
}

function passesDataQuality(task, mode) {
  if (!mode) return true;
  const missingCost = task.labourCost == null;
  const missingEnd = !task.endTime;
  const longDuration = (task.durationMinutes || 0) > 480;
  if (mode === 'missing_cost') return missingCost;
  if (mode === 'missing_end') return missingEnd;
  if (mode === 'long_duration') return longDuration;
  if (mode === 'any') return missingCost || missingEnd || longDuration;
  return true;
}

function passesPerformanceMode(task, mode, benchmarks, allTasks) {
  if (!mode) return true;

  if (mode === 'high_cost') {
    const costs = allTasks.map((t) => t.labourCost).filter((c) => c != null && c > 0);
    const threshold = percentile(costs, 75);
    return threshold != null && task.labourCost != null && task.labourCost >= threshold;
  }

  if (mode === 'slowest') {
    const durations = allTasks.map((t) => t.durationMinutes).filter((d) => d != null && d > 0);
    const threshold = percentile(durations, 75);
    return threshold != null && (task.durationMinutes || 0) >= threshold;
  }

  const benchmark = benchmarks?.get?.(getBenchmarkKey(task));
  const cmp = calcBenchmarkComparison(benchmark, task);
  if (!cmp) return false;

  if (mode === 'best_performing') {
    return cmp.betterWhenLower ? cmp.pctDiff < -10 : cmp.pctDiff > 10;
  }
  if (mode === 'over_expected_time') {
    return cmp.metric === 'duration' && cmp.pctDiff > 15;
  }
  if (mode === 'over_expected_cost') {
    return (cmp.metric === 'costPerUnit' || cmp.metric === 'labourCost') && cmp.pctDiff > 15;
  }

  return true;
}

/**
 * Apply analytics filters to a task array. Used for dashboard cards, charts, tables, and exports.
 *
 * @param {object[]} tasks
 * @param {object} rawFilters
 * @param {{ benchmarks?: Map, issueNoteTaskIds?: Set, dateRange?: { start: Date, end: Date } }} ctx
 */
export function applyAnalyticsFilters(tasks, rawFilters, ctx = {}) {
  const filters = validateAnalyticsFilters(rawFilters);
  const { benchmarks, issueNoteTaskIds, dateRange } = ctx;

  let result = tasks.filter((task) => {
    if (filters.taskType !== 'all' && task.taskType !== filters.taskType) return false;
    if (!matchesSearch(task, filters.search)) return false;

    if (filters.workerName) {
      const w = filters.workerName.toLowerCase();
      if (!task.employeeNames?.some((n) => n.toLowerCase().includes(w))) return false;
    }
    if (filters.roomId && task.roomId !== filters.roomId) return false;
    if (filters.taskCategoryId && task.taskCategoryId !== filters.taskCategoryId) return false;
    if (filters.tagId && !task.tagIds?.includes(filters.tagId)) return false;
    if (filters.productName) {
      const p = filters.productName.toLowerCase();
      if (!task.productName?.toLowerCase().includes(p)) return false;
    }

    if (filters.costMin != null && (task.labourCost == null || task.labourCost < filters.costMin)) return false;
    if (filters.costMax != null && (task.labourCost == null || task.labourCost > filters.costMax)) return false;
    if (filters.durationMin != null && (task.durationMinutes || 0) < filters.durationMin) return false;
    if (filters.durationMax != null && (task.durationMinutes || 0) > filters.durationMax) return false;
    if (filters.costPerUnitMin != null && (task.costPerUnit == null || task.costPerUnit < filters.costPerUnitMin)) return false;
    if (filters.costPerUnitMax != null && (task.costPerUnit == null || task.costPerUnit > filters.costPerUnitMax)) return false;

    if (filters.shift && taskShift(task) !== filters.shift) return false;
    if (filters.hasIssueNotes && issueNoteTaskIds && !issueNoteTaskIds.has(task.id)) return false;
    if (!passesDataQuality(task, filters.dataQuality)) return false;

    if (dateRange && filters.statusScope === 'active') {
      const start = task.startTime?.toDate?.() ?? (task.startTime ? new Date(task.startTime) : null);
      if (!start || start < dateRange.start || start > dateRange.end) return false;
    }

    return true;
  });

  if (filters.performanceMode) {
    const pool = result;
    result = pool.filter((t) => passesPerformanceMode(t, filters.performanceMode, benchmarks, pool));
  }

  const { sortKey, sortDir } = filters;
  return [...result].sort((a, b) => {
    const av = taskSortValue(a, sortKey);
    const bv = taskSortValue(b, sortKey);
    const cmp = av < bv ? -1 : av > bv ? 1 : 0;
    return sortDir === 'asc' ? cmp : -cmp;
  });
}

/** Whether any non-default filter is active (excluding sort). */
export function hasActiveFilters(raw) {
  return !filtersMatch(raw, DEFAULT_ANALYTICS_FILTERS, { ignoreSort: true });
}

/** Detect saved filter criteria that no longer match live reference data. */
export function getStaleFilterHints(filters, { rooms = [], categories = [], tags = [], workers = [] } = {}) {
  const f = validateAnalyticsFilters(filters);
  const hints = [];
  if (f.roomId && !rooms.some((r) => r.id === f.roomId)) hints.push('room');
  if (f.taskCategoryId && !categories.some((c) => c.id === f.taskCategoryId)) hints.push('category');
  if (f.tagId && !tags.some((tg) => tg.id === f.tagId)) hints.push('tag');
  if (f.workerName && !workers.includes(f.workerName)) hints.push('worker');
  return hints;
}

/** Human-readable summary of active non-default filters. */
export function describeActiveFilters(filters, t) {
  const f = validateAnalyticsFilters(filters);
  const parts = [];
  if (f.statusScope === 'active') parts.push(t('analyticsViews.presets.active'));
  if (f.taskType !== 'all') parts.push(f.taskType);
  if (f.workerName) parts.push(f.workerName);
  if (f.roomId) parts.push(t('analyticsViews.byRoom'));
  if (f.tagId) parts.push(t('analyticsViews.byTag'));
  if (f.performanceMode) parts.push(t(`analyticsViews.performance.${f.performanceMode}`));
  if (f.dataQuality) parts.push(t('analyticsViews.presets.missingData'));
  if (f.search) parts.push(`"${f.search}"`);
  return parts;
}
